<template>
  <div class="container">
    <div class="title-banner">
      <h1 class="gb-page-title">Browse Learning Goals</h1>
      <p class="title-subheading">Search through our entire collection of learning goals, objectives, and progress monitoring resources.</p>
    </div>
    <div class="row browse-body">
      <div v-if="fetchingUser" class="text-center text-muted">
        <i class="fa fa-circle-o-notch fa-spin fa-fw"/> Loading...
      </div>
      <div
        class="col-md-3 sticky-panel"
        v-if="!fetchingUser"
        v-stickybits="stickyBitsConfig">
        <BrowseFilterSelector
          :query="unlockedQuery"
        />
      </div>
      <div class="col-md-9" v-if="!fetchingUser">
        <ul class="list-unstyled" v-if="!loadingResults && !(showResults)">
          <li v-if="betaVisibility" class="empty-state-tips">Select a subject and/or a characteristic</li>
          <li v-else class="empty-state-tips">Select a subject from the dropdown</li>
          <li class="empty-state-tips">Filter by grade levels</li>
          <li class="empty-state-tips">Choose a category for the subject selected above</li>
          <li class="empty-state-tips">Filter by a particular standard</li>
          <li class="empty-state-tips">Search using keywords</li>
        </ul>
        <div>
          <BrowseGoalsFiltersDisplay v-if="showResults" :query="unlockedQuery"/>

          <div
            class="no-goals-found"
            v-if="(showResults) && goals.length === 0 && !loadingResults"
            aria-live="polite">
            Sorry, we currently don't have learning goals matching your query; try broadening your search by de-selecting some criteria above.
          </div>
          <BrowseGoalsResults
            v-if="showResults"
            :group-by-subject="!!filters.subject"
            :goals="goals"
            :total-results="totalResults"
            :subject-selected="!!filters.subject"
            :keyword-selected="!!filters.keyword"/>
        </div>
      </div>
    </div>
    <SubmitFeedback
      v-if="!fetchingUser"
      :subject="filters.subject"
      type="Goal Browse"/>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import BrowseFilterSelector from '@/components/search/BrowseFilterSelector'
import BrowseGoalsResults from '@/components/search/BrowseGoalsResults'
import BrowseGoalsFiltersDisplay from '@/components/search/BrowseGoalsFiltersDisplay'
import SubmitFeedback from '@/components/SubmitFeedback'
import { PremiumLocksMixin } from '@/contentprofile/premium_locks_mixin'

export default {
  name: 'BrowseGoalsPage',
  components: {
    BrowseFilterSelector,
    BrowseGoalsResults,
    BrowseGoalsFiltersDisplay,
    SubmitFeedback
  },
  mixins: [PremiumLocksMixin],
  data() {
    return {
      stickyBitsConfig: {
        options: {
          stickyBitStickyOffset: 10,
          useStickyClasses: true
        },
        minWidth: 992
      }
    }
  },
  metaInfo() {
    let title = `${this.unlockedQuery.subject || ''} ${this.unlockedQuery.subjectDomain || ''}`.trim()
    if (this.unlockedQuery.grades) {
      let gradesStr =
        Array.isArray(this.unlockedQuery.grades) ? this.unlockedQuery.grades.join(', ') : this.unlockedQuery.grades
      title = `${title} for grades ${gradesStr}`
    }
    title = `${title || 'Browse'} Goals - Goalbook Toolkit`

    return {
      title: title
    }
  },
  eventContext() {
    return {
      browse_goals_filter_subject: this.filters.subject,
      browse_goals_filter_grades: this.filters.grades,
      browse_goals_filter_category: this.filters.subjectDomain,
      browse_goals_filter_standard: this.filters.standard,
      browse_goals_filter_level_of_support: this.filters.levelOfSupport,
      browse_goals_filter_keyword: this.filters.keyword.slice(0, 120), // limit length due to max size of field in db
    }
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
      goals: state => state.browse.goals,
      totalResults: state => state.browse.totalResults,
      fetchingUser: state => state.user.fetching
    }),
    ...mapGetters({
      loadingResults: 'browse/loadingResults',
      filters: 'browse/filters',
      contentProfileSubjects: 'contentProfile/publishedSubjects',
      betaVisibility: 'visibility/betaVisibility'
    }),
    showResults() {
      const hasSubject =
        !!this.unlockedQuery.subject &&
        this.contentProfileSubjects.some(s => s.subject === this.unlockedQuery.subject)

      const hasKeyword = !!this.unlockedQuery.keyword

      const hasCharacteristics = Array.isArray(this.unlockedQuery.characteristics)
        ? this.unlockedQuery.characteristics.length > 0
        : !!this.unlockedQuery.characteristics

      return hasSubject || hasKeyword || (hasCharacteristics && this.betaVisibility)
    },
    unlockedQuery() {
      return this.filterLockedBrowseGoalsState(this.query)
    }
  },
  props: {
    query: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  created() {
    this.$store.dispatch('browse/fetchGoalCharacteristics');
  },
  mounted() {
    this.$sendEvent('Viewed Browse Goals Page', {
      ref: this.$route.query.ref
    });
  },
  methods: {},
  beforeRouteUpdate(to, from, next) {
    this.$store.commit('browse/setFilters', {
      grades: [],
      subject: '',
      subjectDomain: '',
      standard: '',
      keyword: '',
      levelOfSupport: '',
      characteristics: []
    })
    next()
  }
}
</script>

<style scoped lang="less">
@import '~@/variables.less';
@import '~@/components/search/browsestyles.less';

.empty-state-tips {
  font-weight: 300;
  margin: 25px 0 55px 0;
  font-size: 25px;
  line-height: 40px;
}
.sticky-panel {
  // need this rule because position: sticky causes the filter panel to be layered beneath the goal cards
  z-index: 1;

  @media (min-width: 992px) {
    overflow-y: auto;
    height: ~'calc(100vh - 20px)';
  }
}
.no-goals-found {
  background-color: @blueLight;
  color: @blue;
  padding: 8px 35px 8px 14px;
  border: 1px solid @tealLight;
}
</style>
